import { useCallback } from "react";
import { useStore } from "../store";

const EN = {
  // ── Access codes ──────────────────────────────────────────────
  "accessCodes.aTree": "a tree",
  "accessCodes.accessLevel": "Access level",
  "accessCodes.activeCodes": "Active codes",
  "accessCodes.description": "Generate a code to share with a trusted person. They will use it with your email to request access to",
  "accessCodes.expiration": "Expiration",
  "accessCodes.expired": "Expired",
  "accessCodes.expiresOn": "Expires on",
  "accessCodes.generate": "Generate code",
  "accessCodes.generateFailed": "Unable to generate a code. Please try again.",
  "accessCodes.generateNew": "Generate a new code",
  "accessCodes.loadFailed": "Unable to load access codes.",
  "accessCodes.newCodeCreated": "New code created",
  "accessCodes.noCodes": "No active codes. Generate one to invite someone.",
  "accessCodes.noExpiry": "No expiration",
  "accessCodes.revoke": "Revoke",
  "accessCodes.revokeFailed": "Unable to revoke this code.",
  "accessCodes.shareInstructions": "Share this code with the person. They will need to enter your email",
  "accessCodes.shareInstructions2": "and this code in \"Request access\".",
  "accessCodes.shareWhatsApp": "Share via WhatsApp",
  "accessCodes.someone": "Someone",
  "accessCodes.title": "Access codes",

  // ── Account / profile ─────────────────────────────────────────
  "account.avatarSaveFailed": "Photo uploaded, but the profile could not be updated.",
  "account.avatarUpdated": "Profile photo updated.",
  "account.avatarUploadFailed": "Unable to upload the profile photo.",
  "account.changePassword": "Change password",
  "account.changePhoto": "Change photo",
  "account.confirm": "Confirm",
  "account.deleteButton": "Delete my account...",
  "account.deleteDesc": "This action deletes your account, public profiles, and the trees you own.",
  "account.deleteFailed": "Unable to delete the account.",
  "account.deleteTitle": "Delete account",
  "account.fallbackName": "Denbaya account",
  "account.fullName": "Full name",
  "account.info": "Account information",
  "account.namePlaceholder": "Your name",
  "account.nameRequired": "Full name is required.",
  "account.newPasswordTooShort": "The new password must be at least 6 characters.",
  "account.passwordChangeFailed": "Unable to change the password.",
  "account.passwordChanged": "Password changed.",
  "account.profileUpdateFailed": "Unable to update your profile.",
  "account.profileUpdated": "Profile updated.",
  "account.receiveLink": "Receive a link",
  "account.resetSendFailed": "Unable to send the reset email.",
  "account.resetSent": "Reset email sent.",
  "account.security": "Security",
  "account.securityDesc": "Change your password here, or receive a reset link by email.",
  "account.title": "My profile",
  "account.yesDelete": "Yes, delete",

  // ── Add member ────────────────────────────────────────────────
  "addMember.addPhoto": "Add a photo",
  "addMember.adoptive": "Adoptive",
  "addMember.adoptiveStepFamily": "Adoptive · Step-family, guardianship, etc.",
  "addMember.biological": "Biological",
  "addMember.biologicalBirth": "Biological · Birth",
  "addMember.birthCity": "Birth city",
  "addMember.birthCityPlaceholder": "Ségou",
  "addMember.birthCountry": "Birth country",
  "addMember.birthDate": "Date of birth (optional)",
  "addMember.birthPlace": "Place of birth (optional)",
  "addMember.cancel": "Cancel",
  "addMember.checkDuplicate": "Make sure this is not the same person before adding.",
  "addMember.city": "City",
  "addMember.coParentHint1": "Without another parent, only the link with",
  "addMember.coParentHint2": "is created as shown above. For a two-parent biological filiation, choose the other parent. Otherwise you can complete or specify an adoptive link in the profile.",
  "addMember.coParentLinkNature": "Nature of the link with the other parent",
  "addMember.coParentMustBeSpouse": "The other parent must be a spouse of the selected person.",
  "addMember.connectionChildOf": "Is child of",
  "addMember.connectionSiblingOf": "Is brother / sister of",
  "addMember.connectionNature": "Nature of the link",
  "addMember.connectionParentOf": "Is parent of",
  "addMember.connectionRequired": "Please connect this member to an existing person",
  "addMember.connectionSpouseOf": "Is spouse of",
  "addMember.country": "Country",
  "addMember.currentLocation": "Current location",
  "addMember.currentMarriage": "Current marriage / union",
  "addMember.deathYear": "Year of death (optional)",
  "addMember.deathYearPlaceholder": "e.g. 2008",
  "addMember.email": "Email",
  "addMember.familyLink": "Family link",
  "addMember.filiationReport": "Filiation report with \"Linked to\"",
  "addMember.firstName": "First name",
  "addMember.firstNameRequired": "First name is required",
  "addMember.formerUnion": "Former union (ex-spouse)",
  "addMember.gender": "Gender",
  "addMember.lastName": "Last name",
  "addMember.lastNameRequired": "Last name is required",
  "addMember.linkedTo": "Linked to",
  "addMember.middleName": "Middle name(s) (optional)",
  "addMember.middleNamePlaceholder": "Middle name…",
  "addMember.nickname": "Nickname (optional)",
  "addMember.nicknamePlaceholder": "Nickname, usual name…",
  "addMember.noSpouseRegistered": "No spouse registered for this person — you can link the other parent later in the new child's profile.",
  "addMember.notes": "Notes",
  "addMember.notesPlaceholder": "Biography, anecdotes…",
  "addMember.optional": "optional",
  "addMember.otherParentFather": "Other parent — father (among spouses)",
  "addMember.otherParentMother": "Other parent — mother (among spouses)",
  "addMember.otherParentSpouse": "Other parent — spouse",
  "addMember.phone": "Phone",
  "addMember.possibleDuplicate": "Possible duplicate",
  "addMember.save": "Save",
  "addMember.saving": "Saving…",
  "addMember.siblingFiliation": "Filiation type (with the same parents)",
  "addMember.thisPerson": "this person",
  "addMember.title": "Add a member",
  "addMember.unionType": "Union type",

  // ── Audit log ─────────────────────────────────────────────────
  "audit.by": "By",
  "audit.changedFields": "Changed fields:",
  "audit.countLabel": "change",
  "audit.empty": "No changes recorded for this tree.",
  "audit.recorded": "recorded",
  "audit.title": "Change history",

  // ── Auth ──────────────────────────────────────────────────────
  "auth.alreadyRegistered": "Already registered?",
  "auth.badCredentials": "Incorrect email or password.",
  "auth.createAccount": "Create an account",
  "auth.createMyAccount": "Create my account",
  "auth.creating": "Creating…",
  "auth.email": "Email address",
  "auth.emailPlaceholder": "you@example.com",
  "auth.enterEmailForReset": "Enter your email before requesting a link.",
  "auth.firstName": "First name",
  "auth.forgotPassword": "Forgot password?",
  "auth.lastName": "Last name",
  "auth.loginSubtitle": "Sign in to your family tree",
  "auth.noAccount": "Don't have an account yet?",
  "auth.password": "Password",
  "auth.passwordMismatch": "Passwords do not match.",
  "auth.passwordPlaceholder": "6 characters minimum",
  "auth.passwordTooShort": "Password must be at least 6 characters.",
  "auth.registerFailed": "Could not create the account. This email may already exist.",
  "auth.registerSubtitle": "Create your Denbaya account",
  "auth.resetSendFailed": "Unable to send the reset link.",
  "auth.resetSent": "Reset link sent by email.",
  "auth.signIn": "Sign in",
  "auth.signingIn": "Signing in…",

  // ── Guest banner ──────────────────────────────────────────────
  "banner.backToMyTree": "Back to my tree",
  "banner.sharedTree": "Tree shared with you",

  // ── Claim node ────────────────────────────────────────────────
  "claim.alreadyLinked": "Already linked",
  "claim.alreadyLinkedDesc": "You are already linked to the node",
  "claim.associateToNode": "You want to link yourself to the node:",
  "claim.claimFailed": "Unable to link this node. Please try again.",
  "claim.directClaimDesc": "As a manager of this tree, the association will be immediate — a badge",
  "claim.directSuccess": "Node linked to your profile.",
  "claim.itsMe": "This is me",
  "claim.messageLabel": "Message (optional)",
  "claim.messagePlaceholder": "Explain why you think you are this person…",
  "claim.nodeLinked": "Node linked!",
  "claim.requestDesc": "A request will be sent to the tree owner. Your contact details",
  "claim.requestDesc2": "will be visible to them.",
  "claim.requestSent": "Request sent",
  "claim.requestSuccess": "The tree owner will receive your request and can approve or reject it.",
  "claim.sendRequest": "Send request",
  "claim.title": "Link my identity to this node",

  // ── Comments ──────────────────────────────────────────────────
  "comments.addFailed": "Unable to add the comment.",
  "comments.empty": "No comments yet. Be the first!",
  "comments.loadFailed": "Unable to load comments.",
  "comments.placeholder": "Add a comment… (Enter to send)",
  "comments.title": "Comments",

  // ── Common ────────────────────────────────────────────────────
  "common.accept": "Accept",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.confirm": "Confirm",
  "common.copy": "Copy",
  "common.creating": "Creating…",
  "common.delete": "Delete",
  "common.deleting": "Deleting...",
  "common.edit": "Edit",
  "common.genericError": "Something went wrong. Please try again.",
  "common.loading": "Loading…",
  "common.reject": "Reject",
  "common.save": "Save",
  "common.saving": "Saving…",
  "common.sending": "Sending...",
  "common.sendingEllipsis": "Sending…",
  "common.unknown": "Unknown",
  "common.updating": "Updating...",
  "common.you": "You",

  // ── Edit profile ──────────────────────────────────────────────
  "editProfile.adoptive": "Adoptive",
  "editProfile.adoptiveLink": "Adoptive link",
  "editProfile.biological": "Biological",
  "editProfile.birthCity": "Birth city",
  "editProfile.birthCityPlaceholder": "Ségou",
  "editProfile.birthCountry": "Birth country",
  "editProfile.birthDate": "Date of birth (optional)",
  "editProfile.birthPlace": "Place of birth (optional)",
  "editProfile.cancel": "Cancel",
  "editProfile.changePhoto": "Change photo",
  "editProfile.city": "City",
  "editProfile.country": "Country",
  "editProfile.currentLocation": "Current location",
  "editProfile.currentSpouses": "Current spouse(s)",
  "editProfile.deathYear": "Year of death (optional)",
  "editProfile.email": "Email",
  "editProfile.familyLinkError": "Unable to update family links.",
  "editProfile.familyLinks": "Family links",
  "editProfile.firstName": "First name",
  "editProfile.firstNameRequired": "First name is required",
  "editProfile.former": "Former",
  "editProfile.formerSpouses": "Former spouse(s)",
  "editProfile.gender": "Gender",
  "editProfile.lastName": "Last name",
  "editProfile.lastNameRequired": "Last name is required",
  "editProfile.middleName": "Middle name(s) (optional)",
  "editProfile.middleNamePlaceholder": "Middle name…",
  "editProfile.nickname": "Nickname (optional)",
  "editProfile.nicknamePlaceholder": "Nickname, usual name…",
  "editProfile.married": "Married",
  "editProfile.notes": "Notes",
  "editProfile.parents": "Parents (max 2)",
  "editProfile.phone": "Phone",
  "editProfile.save": "Save",
  "editProfile.spouses": "Spouse(s)",
  "editProfile.saveError": "Unable to save changes.",
  "editProfile.spouseConflict": "A person cannot be both a current and former spouse.",
  "editProfile.titlePrefix": "Edit",
  "editProfile.unionUpdateError": "Unable to update unions.",
  "editProfile.uploading": "Uploading…",

  // ── Equivalence / cross-tree links ────────────────────────────
  "equivalence.inLinkedTree": "In the linked tree",
  "equivalence.inMyTree": "In my tree",
  "equivalence.intro":
    "Link two profiles in different trees: say whether they are the same person or another family relationship. Each tree keeps its data until the link is approved.",
  "equivalence.loadPeopleFailed": "Unable to load people from this tree.",
  "equivalence.modalTitle": "Propose a cross-tree link",
  "equivalence.noLinkedTrees": "No linked tree available.",
  "equivalence.noResults": "No results",
  "equivalence.proposalSent": "Proposal sent",
  "equivalence.proposalSentDesc": "will receive your proposal and can accept or reject it. Each tree keeps its own data — no automatic changes are made.",
  "equivalence.relationDetailLabel": "Specify the relationship",
  "equivalence.relationDetail.parent_child": "Direct kinship (parent / child / line)",
  "equivalence.relationDetail.in_law": "In-law relationship (extended in-law family)",
  "equivalence.relationDetail.sibling_branch": "Siblings or parallel branch",
  "equivalence.relationDetail.cousin": "Cousin relationship",
  "equivalence.relationDetail.step_family": "Blended family / stepchildren",
  "equivalence.relationDetail.guardian_adoption": "Guardianship, adoption, or foster tie",
  "equivalence.relationDetail.other": "Other (specify below)",
  "equivalence.relationHint": "The other tree's owner will see the proposal and can accept or reject it.",
  "equivalence.relationLabel": "Link type",
  "equivalence.relationOtherPlaceholder": "Describe the link (required)…",
  "equivalence.relationRelated": "Other family tie (kinship, in-law, etc.)",
  "equivalence.relationSame": "Same person in two trees",
  "equivalence.searchPlaceholder": "Search for a person in this tree…",
  "equivalence.sendProposal": "Send proposal",
  "equivalence.treeOwner": "The tree owner",
  "equivalence.typeToSearch": "Type at least 2 characters to search.",

  // ── Export ────────────────────────────────────────────────────
  "export.chooseFormat": "Choose a format to download your family tree.",
  "export.close": "Close",
  "export.defaultTreeName": "tree",
  "export.done": "done.",
  "export.error": "Export error:",
  "export.exporting": "Exporting…",
  "export.gedcomDesc": "Standard format for importing into other genealogy software.",
  "export.gedcomLabel": "GEDCOM (.ged)",
  "export.pdfDesc": "The tree in high resolution, ready to print.",
  "export.pdfLabel": "Document (PDF)",
  "export.pngDesc": "Capture of the tree as it appears on screen.",
  "export.pngLabel": "Image (PNG)",
  "export.success": "Export",
  "export.title": "Export tree",

  // ── Invite ────────────────────────────────────────────────────
  "invite.accessLevel": "Access level",
  "invite.adminNonUser": "This person must create a Denbaya account before receiving an admin invitation.",
  "invite.afterAccept": "as soon as they accept.",
  "invite.canViewAfterAccept": "They will be able to view",
  "invite.cannotInviteHere": "You cannot invite from this screen.",
  "invite.emailAndNotif": "an email and a notification",
  "invite.emailAndNotifDesc": "This person will receive an email and a notification.",
  "invite.emailFailed": "The invitation was created, but the email could not be sent.",
  "invite.emailLabel": "Person's email",
  "invite.invalidEmail": "Invalid email address.",
  "invite.lookupFailed": "Unable to verify this account. Please try again.",
  "invite.memberFallback": "A Denbaya member",
  "invite.notifDesc": "This person will receive a Denbaya notification.",
  "invite.notifMessage": "invites you to view",
  "invite.notifOnly": "a notification",
  "invite.selfInvite": "You cannot invite yourself.",
  "invite.send": "Send invitation",
  "invite.sentTitle": "Invitation sent",
  "invite.sentToast": "Invitation sent.",
  "invite.title": "Invite someone",
  "invite.toAccess": "to access",
  "invite.treeFallback": "a family tree",
  "invite.willReceive": "will receive",
  "invite.yourTree": "your tree",

  // ── Invite landing ────────────────────────────────────────────
  "inviteLanding.description": "Sign in or create an account to open this family tree shared by",
  "inviteLanding.invalid": "This link is invalid or expired.",
  "inviteLanding.invitedToView": "You are invited to view",
  "inviteLanding.loading": "Loading invitation...",
  "inviteLanding.title": "Denbaya Invitation",

  // ── Tree legend ───────────────────────────────────────────────
  "legend.exUnion": "Former union (ex-spouse)",
  "legend.exUnionShort": "Ex-union",
  "legend.filiation": "Filiation",
  "legend.filiationTitle": "Parent–child link",
  "legend.hintClick": "Click = profile",
  "legend.hintDoubleClick": "Double-click = center",
  "legend.hintDoubleTap": "Double-tap = center",
  "legend.hintMultiUnion": "Multiple gold lines = multiple unions",
  "legend.hints": "· Multiple gold lines = multiple unions · Click = profile · Double-click = center",
  "legend.hintsMobile": "· Tap = profile · Double-tap = center",
  "legend.hintTap": "Tap = profile",
  "legend.union": "Union (spouse)",
  "legend.unionShort": "Union",

  // ── Tree node ─────────────────────────────────────────────────
  "node.clickHint": "Click to view · Double-click to set as tree root",
  "node.deceased": "DECEASED",
  "node.linkPendingBadge": "PENDING",
  "node.linkedBadge": "LINKED",
  "node.meBadge": "ME",

  // ── Notifications ─────────────────────────────────────────────
  "notif.accepted": "✓ Accepted",
  "notif.actionFailed": "Unable to process this notification.",
  "notif.clear": "Clear",
  "notif.dismissed": "Dismissed",
  "notif.empty": "No notifications",
  "notif.linkKind.related": "Other family tie",
  "notif.linkKind.same": "Same person",
  "notif.pending": "pending",
  "notif.rejected": "✗ Rejected",
  "notif.relationKindCaption": "Link type",
  "notif.resolvedNotification": "resolved notification",
  "notif.title": "Notifications",
  "notif.tooltip": "Notifications",

  // ── Profile panel ─────────────────────────────────────────────
  "profile.addFamilyLinks": "Add family links",
  "profile.bridgeConfirmed": "Confirmed link",
  "profile.bridgeConfirmedRestricted": "Confirmed link – restricted access",
  "profile.bridgeIdenticalTo": "Identical to a node in",
  "profile.bridgeLink": "Cross-tree link",
  "profile.bridgePending": "Pending proposal",
  "profile.bridgePendingDesc": "This person has been proposed as equivalent to a node in",
  "profile.bridgePendingWaiting": "Waiting for approval by the tree owner.",
  "profile.bridgeRestrictedDesc": "Identical to a node in",
  "profile.bridgeRestrictedSuffix": ", but you do not have access to that tree yet.",
  "profile.bridgeView": "View",
  "profile.centerTree": "Center tree here",
  "profile.claimNode": "This is me",
  "profile.deleteConfirm": "Delete",
  "profile.deleteConfirmSuffix": "and all descendants?",
  "profile.deleting": "Deleting…",
  "profile.directFamily": "Immediate family",
  "profile.email": "Email",
  "profile.location": "Location",
  "profile.noContact": "No contact details provided.",
  "profile.noRelatives": "No direct relationship found in this tree.",
  "profile.phone": "Phone",
  "profile.proposeLink": "Propose a link",
  "profile.rootNode": "Central node",

  // ── Relationships ─────────────────────────────────────────────
  "rel.adoptiveDaughter": "Adoptive daughter",
  "rel.adoptiveFather": "Adoptive father",
  "rel.adoptiveMother": "Adoptive mother",
  "rel.adoptiveSon": "Adoptive son",
  "rel.brother": "Brother",
  "rel.cousinF": "Cousin (f.)",
  "rel.cousinFemale": "Cousin (f.)",
  "rel.cousinM": "Cousin (m.)",
  "rel.cousinMale": "Cousin (m.)",
  "rel.daughter": "Daughter",
  "rel.exHusband": "Ex-husband",
  "rel.exWife": "Ex-wife",
  "rel.father": "Father",
  "rel.firstHusband": "1st husband",
  "rel.firstWife": "1st wife",
  "rel.granddaughter": "Granddaughter",
  "rel.grandfather": "Grandfather",
  "rel.grandmother": "Grandmother",
  "rel.grandson": "Grandson",
  "rel.halfBrother": "Half-brother",
  "rel.halfSister": "Half-sister",
  "rel.husband": "Husband",
  "rel.husbandOrdinal": "husband",
  "rel.mother": "Mother",
  "rel.nephew": "Nephew",
  "rel.niece": "Niece",
  "rel.sister": "Sister",
  "rel.son": "Son",
  "rel.uncle": "Uncle",
  "rel.aunt": "Aunt",
  "rel.wife": "Wife",
  "rel.wifeOrdinal": "wife",

  // ── Password reset ────────────────────────────────────────────
  "reset.back": "Back to Denbaya",
  "reset.changePassword": "Change password",
  "reset.confirmPassword": "Confirm password",
  "reset.done": "Your password has been changed.",
  "reset.invalidLink": "The link is expired or invalid. Request a new link.",
  "reset.newPassword": "New password",
  "reset.subtitle": "Choose a new password for your Denbaya account.",
  "reset.title": "New password",

  // ── Roles ─────────────────────────────────────────────────────
  "role.admin": "Full access",
  "role.adminOption": "Full access — can edit, delete, and manage access",
  "role.edit": "Edit",
  "role.editOption": "Edit — can add and update members",
  "role.propose": "Propose links",
  "role.proposeOption": "Propose links — can suggest equivalences",
  "role.view": "Read-only",
  "role.viewOption": "Read-only — can view the tree",
  "roles.proposeShort": "can propose links",
  "roles.viewShort": "read-only",

  // ── Search ────────────────────────────────────────────────────
  "search.noResults": "No results for",
  "search.placeholder": "Search for a family member…",

  // ── Settings ──────────────────────────────────────────────────
  "settings.access": "Tree access",
  "settings.accessCodes": "Access codes",
  "settings.audit": "View change history",
  "settings.cancel": "Cancel",
  "settings.cancelBtn": "Cancel",
  "settings.changeAccessLevel": "Change access level",
  "settings.close": "Close",
  "settings.confirm": "Confirm?",
  "settings.currentTree": "Current tree",
  "settings.darkTheme": "Dark theme",
  "settings.darkThemeDesc": "Change the app appearance",
  "settings.deleteTree": "Delete this tree",
  "settings.deleteTreeBtn": "Delete tree…",
  "settings.deleteTreeDesc1": "Permanently deletes",
  "settings.deleteTreeDesc2": "and all people it contains. This action is irreversible.",
  "settings.deleting": "Deleting…",
  "settings.inviteEmail": "Invite by email",
  "settings.language": "Language",
  "settings.languageDesc": "Interface in French or English",
  "settings.leave": "Leave…",
  "settings.leaveTree": "Leave this tree",
  "settings.leaveTreeDesc1": "You will no longer have access to",
  "settings.leaveTreeDesc2": "The owner will need to re-invite you to regain access.",
  "settings.noAccess": "No additional access yet.",
  "settings.notifications": "Notifications",
  "settings.notificationsDesc": "Link requests, invitations, updates",
  "settings.privacyError": "Unable to update privacy.",
  "settings.privacyUpdated": "Tree privacy updated.",
  "settings.privateTree": "Private tree",
  "settings.privateTreeDesc": "Only invited people can view your tree",
  "settings.rename": "Rename",
  "settings.revokeAccess": "Revoke access",
  "settings.saveBtn": "Save",
  "settings.saveError": "Unable to save this setting.",
  "settings.saved": "Setting saved.",
  "settings.title": "Settings",
  "settings.typeDeleteConfirm": "Type",
  "settings.yesDelete": "Yes, delete",
  "settings.yesLeave": "Yes, leave",

  // ── Share ─────────────────────────────────────────────────────
  "share.copied": "Copied!",
  "share.copy": "Copy",
  "share.copyFailed": "Unable to copy the link automatically.",
  "share.description": "Share this link so a family member can view your tree in read-only mode. The link is valid for 30 days.",
  "share.generateFailed": "Unable to generate the link. Please try again.",
  "share.generating": "Generating link…",
  "share.invalidLink": "This share link is invalid or expired.",
  "share.noTree": "No tree selected",
  "share.noTreeDescription": "Select or create a tree before generating a share link.",
  "share.title": "Share tree",

  // ── Sidebar ───────────────────────────────────────────────────
  "sidebar.actions": "Actions",
  "sidebar.addMember": "Add a member",
  "sidebar.connected": "Connected",
  "sidebar.createMyTree": "Create my tree",
  "sidebar.createTreeConfirm": "Create",
  "sidebar.createTreePlaceholder": "e.g. Diallo family",
  "sidebar.export": "Export",
  "sidebar.invite": "Invite someone",
  "sidebar.linkedTrees": "Linked Trees",
  "sidebar.logout": "Log out",
  "sidebar.members": "Family members",
  "sidebar.myFamilyProfile": "My family profile",
  "sidebar.myTrees": "My Trees",
  "sidebar.noTrees": "No trees yet — use the center area to create one.",
  "sidebar.noTreesGuest": "You can keep your own tree while viewing trees others share with you.",
  "sidebar.requestAccess": "+ Request access",
  "sidebar.settings": "Settings",

  // ── Status ────────────────────────────────────────────────────
  "status": "in",

  // ── Top bar ───────────────────────────────────────────────────
  "topbar.add": "Add",
  "topbar.share": "Share",

  // ── Tree canvas ───────────────────────────────────────────────
  "tree.addFirstMember": "+ Add a first member",
  "tree.addFirstMemberHint": "Start by adding a first family member.",
  "tree.create": "Create",
  "tree.createFirstTree": "Create your first tree",
  "tree.creating": "Creating…",
  "tree.emptyTree": "Your tree is empty",
  "tree.nameYourTree": "Name your family tree to get started.",
  "tree.treeNamePlaceholder": "e.g. Doumbia family",

  // ── Tree link / request access ────────────────────────────────
  "treeLink.accessCode": "access code",
  "treeLink.accessCodeLabel": "Access code",
  "treeLink.instructions": "Ask the tree owner to generate an",
  "treeLink.instructions2": "(Settings → Access codes). They can then share it with you by message.",
  "treeLink.invalidCode": "The access code must contain 8 characters (format XXXX-XXXX).",
  "treeLink.invalidEmail": "Please enter the owner's email.",
  "treeLink.lookupFailed": "Invalid code or email. Check with the tree owner.",
  "treeLink.messageLabel": "Message (optional)",
  "treeLink.messagePlaceholder": "Introduce yourself and explain your connection to this family…",
  "treeLink.ownerEmail": "Owner's email",
  "treeLink.requestSent": "Request sent",
  "treeLink.requestSentDesc1": "The owner of",
  "treeLink.requestSentDesc2": "will receive your request for",
  "treeLink.requestSentDesc3": "You will be notified once it is accepted.",
  "treeLink.sendRequest": "Send request",
  "treeLink.title": "Request access to a tree",
  "treeLink.verifying": "Checking…",
};

const EN_FALLBACKS = {
  "Email ou mot de passe incorrect.": "Incorrect email or password.",
  "Entrez votre email avant de demander un lien.": "Enter your email before requesting a link.",
  "Impossible d'envoyer le lien de réinitialisation.": "Unable to send the reset link.",
  "Lien de réinitialisation envoyé par email.": "Reset link sent by email.",
  "Adresse e-mail": "Email address",
  "Mot de passe": "Password",
  "Envoi...": "Sending...",
  "Mot de passe oublié ?": "Forgot password?",
  "Connexion…": "Signing in...",
  "Se connecter": "Sign in",
  "Pas encore de compte ?": "Don't have an account yet?",
  "Créer un compte": "Create an account",
  "Le mot de passe doit faire au moins 6 caractères.": "Password must be at least 6 characters.",
  "Erreur lors de la création du compte. Cet email existe peut-être déjà.": "Could not create the account. This email may already exist.",
  "Prénom": "First name",
  "Nom": "Last name",
  "6 caractères minimum": "6 characters minimum",
  "Création…": "Creating...",
  "Créer mon compte": "Create my account",
  "Déjà inscrit ?": "Already registered?",
  "Connectez-vous à votre arbre familial": "Sign in to your family tree",
  "Créez votre compte Denbaya": "Create your Denbaya account",
  "Le lien est expiré ou invalide. Demandez un nouveau lien.": "The link is expired or invalid. Request a new link.",
  "Les mots de passe ne correspondent pas.": "Passwords do not match.",
  "Nouveau mot de passe": "New password",
  "Choisissez un nouveau mot de passe pour votre compte Denbaya.": "Choose a new password for your Denbaya account.",
  "Votre mot de passe a été modifié.": "Your password has been changed.",
  "Retour à Denbaya": "Back to Denbaya",
  "Confirmer le mot de passe": "Confirm password",
  "Modification...": "Updating...",
  "Modifier le mot de passe": "Change password",
  "Chercher un membre de la famille…": "Search for a family member...",
  "Aucun résultat pour": "No results for",
  "peut proposer des liens": "can propose links",
  "lecture seule": "read-only",
  "Arbre partagé avec vous": "Tree shared with you",
  "Revenir à mon arbre": "Back to my tree",
  "Partager l'arbre": "Share tree",
  "Impossible de générer le lien. Veuillez réessayer.": "Unable to generate the link. Please try again.",
  "Impossible de copier le lien automatiquement.": "Unable to copy the link automatically.",
  "Partagez ce lien pour permettre à un membre de la famille de voir votre arbre en lecture seule. Le lien est valable 30 jours.": "Share this link so a family member can view your tree in read-only mode. The link is valid for 30 days.",
  "Sélectionnez ou créez un arbre avant de générer un lien de partage.": "Select or create a tree before generating a share link.",
  "Génération du lien…": "Generating link...",
  "Aucun arbre sélectionné": "No tree selected",
  "Copié !": "Copied!",
  "Copier": "Copy",
  "Historique des modifications": "Change history",
  "Ajout": "Added",
  "Modification": "Modified",
  "Suppression": "Deleted",
  "Champs modifiés :": "Changed fields:",
  "Par": "By",
  "Aucune modification enregistrée pour cet arbre.": "No changes recorded for this tree.",
  "modification": "change",
  "enregistrée": "recorded",
  "Fermer": "Close",
  "Accepter": "Accept",
  "Refuser": "Reject",
  "Enregistrer": "Save",
  "Annuler": "Cancel",
  "Inconnu": "Unknown",
  "Vous": "You",
  "Suppression...": "Deleting...",
  "Invitation Denbaya": "Denbaya Invitation",
  "Chargement de l'invitation...": "Loading invitation...",
  "Ce lien est invalide ou expiré.": "This link is invalid or expired.",
  "Vous êtes invité à consulter": "You are invited to view",
  "Connectez-vous ou créez un compte pour ouvrir cet arbre familial partagé par": "Sign in or create an account to open this family tree shared by",
  "Notifications": "Notifications",
  "en attente": "pending",
  "Aucune notification": "No notifications",
  "Impossible de traiter cette notification.": "Unable to process this notification.",
  "Effacer": "Clear",
  "notification traitée": "resolved notification",
  "✓ Acceptée": "✓ Accepted",
  "Ignorée": "Dismissed",
  "✗ Refusée": "✗ Rejected",
  "Proposition de lien": "Link proposal",
  "Demande d'identité": "Identity request",
  "Demande d'accès": "Access request",
  "Invitation d'accès": "Access invitation",
  "Accès accordé": "Access granted",
  "Commentaires": "Comments",
  "Impossible de charger les commentaires.": "Unable to load comments.",
  "Impossible d'ajouter le commentaire.": "Unable to add comment.",
  "Aucun commentaire. Soyez le premier !": "No comments yet. Be the first!",
  "Ajouter un commentaire… (Entrée pour envoyer)": "Add a comment... (Enter to send)",
  "Partager": "Share",
  "Ajouter": "Add",
  "Mon profil": "My profile",
  "Le nom complet est requis.": "Full name is required.",
  "Impossible de mettre à jour votre profil.": "Unable to update your profile.",
  "Profil mis à jour.": "Profile updated.",
  "Impossible d'envoyer la photo de profil.": "Unable to upload the profile photo.",
  "Photo envoyée, mais impossible de mettre à jour le profil.": "Photo uploaded, but the profile could not be updated.",
  "Photo de profil mise à jour.": "Profile photo updated.",
  "Le nouveau mot de passe doit faire au moins 6 caractères.": "The new password must be at least 6 characters.",
  "Impossible de modifier le mot de passe.": "Unable to change the password.",
  "Mot de passe modifié.": "Password changed.",
  "Impossible d'envoyer l'email de réinitialisation.": "Unable to send the reset email.",
  "Email de réinitialisation envoyé.": "Reset email sent.",
  "Impossible de supprimer le compte.": "Unable to delete the account.",
  "Informations du compte": "Account information",
  "Compte Denbaya": "Denbaya account",
  "Changer la photo": "Change photo",
  "Nom complet": "Full name",
  "Votre nom": "Your name",
  "Sécurité": "Security",
  "Modifiez votre mot de passe ici, ou recevez un lien de réinitialisation par email.": "Change your password here, or receive a reset link by email.",
  "Changer le mot de passe": "Change password",
  "Recevoir un lien": "Receive a link",
  "Supprimer le compte": "Delete account",
  "Cette action supprime votre compte, vos profils publics et les arbres dont vous êtes propriétaire.": "This action deletes your account, public profiles, and the trees you own.",
  "Supprimer mon compte...": "Delete my account...",
  "Confirmer ?": "Confirm?",
  "Oui, supprimer": "Yes, delete",
  "Adresse email invalide.": "Invalid email address.",
  "Vous ne pouvez pas inviter depuis cet écran.": "You cannot invite from this screen.",
  "Vous ne pouvez pas vous inviter vous-même.": "You cannot invite yourself.",
  "Impossible de vérifier ce compte. Veuillez réessayer.": "Unable to verify this account. Please try again.",
  "Cette personne doit d'abord créer un compte Denbaya pour recevoir une invitation d'administrateur.": "This person must create a Denbaya account before receiving an admin invitation.",
  "Un membre Denbaya": "A Denbaya member",
  "vous invite à consulter": "invites you to view",
  "un arbre familial": "a family tree",
  "Une erreur est survenue. Veuillez réessayer.": "Something went wrong. Please try again.",
  "L'invitation a été créée, mais l'email n'a pas pu être envoyé.": "The invitation was created, but the email could not be sent.",
  "Invitation envoyée.": "Invitation sent.",
  "Inviter quelqu'un": "Invite someone",
  "Envoi…": "Sending...",
  "Envoyer l'invitation": "Send invitation",
  "Invitation envoyée": "Invitation sent",
  "recevra": "will receive",
  "un email et une notification": "an email and a notification",
  "une notification": "a notification",
  "pour accéder à": "to access",
  "La personne recevra un email et une notification.": "This person will receive an email and a notification.",
  "La personne recevra une notification dans Denbaya.": "This person will receive a Denbaya notification.",
  "Elle pourra consulter": "They will be able to view",
  "votre arbre": "your tree",
  "dès qu'elle l'accepte.": "as soon as they accept.",
  "Email de la personne": "Person's email",
  "Niveau d'accès": "Access level",
  "Lecture seule — peut consulter l'arbre": "Read-only — can view the tree",
  "Proposer des liens — peut suggérer des équivalences": "Propose links — can suggest equivalences",
  "Édition — peut ajouter et modifier des membres": "Edit — can add and update members",
  "Accès complet — peut modifier, supprimer et gérer les accès": "Full access — can edit, delete, and manage access",
  "Filtrer par nom, ville…": "Filter by name, city...",
  "Le prénom est requis": "First name is required",
  "Impossible de mettre à jour les liens familiaux.": "Unable to update family links.",
  "Année de naissance": "Birth year",
  "Année de décès": "Death year",
  "Téléphone": "Phone",
  "Époux / Épouse(s)": "Spouse(s)",
  "Impossible d'associer ce nœud. Veuillez réessayer.": "Unable to associate this node. Please try again.",
  "Nœud associé à votre profil.": "Node linked to your profile.",
  "Le propriétaire de l'arbre recevra votre demande et pourra l'approuver ou la refuser.": "The tree owner will receive your request and can approve or reject it.",
  "Associer mon identité à ce nœud": "Link my identity to this node",
  "Nœud associé !": "Node linked!",
  "Demande envoyée": "Request sent",
  "Expliquez pourquoi vous pensez être cette personne…": "Explain why you think you are this person...",
  "Impossible d'enregistrer ce paramètre.": "Unable to save this setting.",
  "Paramètre enregistré.": "Setting saved.",
  "Confidentialité de l'arbre mise à jour.": "Tree privacy updated.",
  "Impossible de mettre à jour la confidentialité.": "Unable to update privacy.",
  "Changer le niveau d'accès": "Change access level",
  "Révoquer l'accès": "Revoke access",
  "Langue": "Language",
  "Interface en français ou en anglais": "Interface in French or English",
  "Demandes de lien, invitations, mises à jour": "Link requests, invitations, updates",
  "Arbre privé": "Private tree",
  "Seules les personnes invitées peuvent voir votre arbre": "Only invited people can view your tree",
  "Thème sombre": "Dark theme",
  "Change l'apparence de l'application": "Change the app appearance",
  "Accès à cet arbre": "Tree access",
  "Aucun accès supplémentaire pour l'instant.": "No additional access yet.",
  "Codes d'accès": "Access codes",
  "Proposer un lien d'équivalence": "Propose an equivalence link",
  "Le propriétaire de l'arbre": "The tree owner",
  "Aucun arbre lié disponible.": "No linked tree available.",
  "Rechercher une personne dans cet arbre…": "Search for a person in this tree...",
  "Aucun résultat": "No results",
  "Tapez au moins 2 caractères pour rechercher.": "Type at least 2 characters to search.",
  "Expiré": "Expired",
  "Révoquer": "Revoke",
  "Impossible de charger les codes d'accès.": "Unable to load access codes.",
  "Impossible de générer un code. Veuillez réessayer.": "Unable to generate a code. Please try again.",
  "Impossible de révoquer ce code.": "Unable to revoke this code.",
  "Édition": "Edit",
  "Accès complet": "Full access",
  "Générer le code": "Generate code",
  "Créer": "Create",
  "ex. Famille Doumbia": "e.g. Doumbia family",
  "Union (époux / épouse)": "Union (spouse)",
  "Union dissoute (ex-conjoint·e)": "Former union (ex-spouse)",
  "Ex-épouse": "Ex-wife",
  "Ex-époux": "Ex-husband",
  "Mère adoptive": "Adoptive mother",
  "Père adoptif": "Adoptive father",
  "Fille adoptive": "Adoptive daughter",
  "Fils adoptif": "Adoptive son",
  "Lien biologique": "Biological parent",
  "Lien adoptif": "Adoptive parent",
  "Conjoint·e·s actuel·le·s": "Current spouse(s)",
  "Ancien·ne·s conjoint·e·s": "Former spouse(s)",
  "Une personne ne peut pas être à la fois conjoint·e actuel·le et ancien·ne.": "A person cannot be both a current and former spouse.",
  "Chercher un membre…": "Search for a member...",
  "Mère": "Mother",
  "Père": "Father",
  "Épouse": "Wife",
  "Époux": "Husband",
  "Sœur": "Sister",
  "Frère": "Brother",
  "Fille": "Daughter",
  "Fils": "Son",
  "Localisation": "Location",
  "Famille directe": "Immediate family",
  "Aucune coordonnée renseignée.": "No contact details provided.",
  "Aucune relation directe trouvée dans cet arbre.": "No direct relationship found in this tree.",
  "Ajouter des liens familiaux": "Add family links",
  "Nœud central": "Central node",
  "Modifier": "Edit",
  "Supprimer": "Delete",
  "Confirmer": "Confirm",
  "Centrer l'arbre ici": "Center tree here",
  "C'est moi": "This is me",
  "Proposer un lien": "Propose a link",
  "Est époux / épouse de": "Is spouse of",
  "Veuillez relier ce membre à une personne existante": "Please connect this member to an existing person",
  "Optionnel": "Optional",
  "Biographie, anecdotes…": "Biography, anecdotes...",
  "Lié à": "Linked to",
  "Veuillez entrer l'email du propriétaire.": "Please enter the owner's email.",
  "Le code d'accès doit contenir 8 caractères (format XXXX-XXXX).": "The access code must contain 8 characters (format XXXX-XXXX).",
  "Code ou email invalide. Vérifiez auprès du propriétaire de l'arbre.": "Invalid code or email. Check with the tree owner.",
  "Demander l'accès à un arbre": "Request access to a tree",
  "Vérification…": "Checking...",
  "Envoyer la demande": "Send request",
  "Demandez à la personne propriétaire de l'arbre de générer un": "Ask the tree owner to generate an",
  "code d'accès": "access code",
  "Elle pourra ensuite vous le partager par message.": "They can then share it with you by message.",
  "Présentez-vous et expliquez votre lien avec cette famille…": "Introduce yourself and explain your connection to this family...",
  "LIÉ": "LINKED",
  "ACCÈS REFUSÉ": "ACCESS DENIED",
  "Membres de la famille": "Family members",
  "Ajouter un membre": "Add a member",
  "Trier : Nom": "Sort: Name",
  "Trier : Naissance": "Sort: Birth",
  "Trier : Ville": "Sort: City",
  "Décédé·e": "Deceased",
  "Aucun résultat pour «": "No results for \"",
  "Chargement…": "Loading…",
  "Enregistrement…": "Saving…",
  "Suppression…": "Deleting…",
};

export function t(language, key, fallback) {
  if (language === "en") return EN[key] ?? EN_FALLBACKS[fallback] ?? fallback;
  return fallback;
}

export function translateLiteral(language, value) {
  if (language !== "en" || typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return value;
  const translated = EN_FALLBACKS[trimmed];
  if (!translated) return value;
  return value.replace(trimmed, translated);
}

export function useT() {
  const language = useStore((s) => s.currentUser?.language ?? "fr");
  return useCallback((key, fallback) => t(language, key, fallback), [language]);
}
